/*
 * diorin-currency.js — location-based DISPLAY currency conversion.
 *
 * Base prices stay in INR (paise) as <span class="money" data-cents="...">.
 * This rewrites them to the visitor's local currency.
 *
 * Pricing model: international markup lives in Shopify Markets (catalog price
 * adjustment, e.g. +40%, priced in INR). When a visitor's country belongs to a
 * configured market, we switch their storefront context via POST /localization
 * (one reload, cookie persists a year) so Liquid renders the market-adjusted
 * INR price. This script then converts that INR amount at the PURE live FX
 * rate from open.er-api.com (INR base, updated daily, cached 12h) — no markup
 * here. Countries in no market get the plain India price converted the same
 * way. settings.fx_rates (manual CODE:rate list) is only the fallback when
 * the rate feed is unreachable on a cold cache.
 *
 * Checkout routing: Shiprocket (Fastrr) Buy Now is India-only. Outside India
 * the PDP/sticky Buy Now and the cart-drawer checkout button route to native
 * Shopify checkout (Shiprocket buttons hidden via CSS in theme.liquid).
 *
 * Country detection order:
 *   1. Shopify localization.country (server-side, if Markets resolved a foreign
 *      country) — window.Diorin.fx.rate already set by Liquid.
 *   2. Shopify /browsing_context_suggestions.json — same-origin geo-IP (no rate
 *      limits, not adblocked, respects VPN), cached per browser session.
 *   3. fallback: outside India with no configured rate → USD; India → INR.
 *
 * Checkout currency is NOT handled here (stays INR). Phase-2 custom checkout
 * should reuse window.Diorin.formatMoney().
 *
 * ponytail: country→currency is a small static map; unlisted countries get USD,
 * which is the intended business rule anyway. Body-wide MutationObserver is the
 * catch-all for AJAX price surfaces (cart drawer, quick-add, search).
 */
(function () {
  var cfg = window.Diorin || (window.Diorin = {});
  var fx = cfg.fx || (cfg.fx = {});
  var rates = cfg.rates || (cfg.rates = {});
  var serverCountry = fx.country; // context Liquid rendered this page in (before any client-side detection)
  var DEBUG = /[?&]debug/.test(location.search);

  function log() { if (DEBUG) console.log.apply(console, ['[Diorin FX]'].concat([].slice.call(arguments))); }

  function inr(cents) {
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  function active() {
    return !!fx.rate && fx.country !== 'IN' && !!fx.currency && fx.currency !== 'INR';
  }

  function formatMoney(cents) {
    cents = Number(cents) || 0;
    if (!active()) return inr(cents);
    var value = Math.round((cents / 100) * fx.rate); // whole units for clean intl display
    var sym = fx.symbol || '';
    var out = sym + value.toLocaleString('en-US');
    if (fx.showCode && sym.indexOf(fx.currency) === -1) out += ' ' + fx.currency;
    return out;
  }

  function convertEl(el) {
    if (el.dataset.cents == null) return;
    el.textContent = formatMoney(el.dataset.cents);
  }

  var mo = null, moTimer;
  function observe() { if (mo) mo.observe(document.body, { childList: true, subtree: true }); }

  function convertAll(root) {
    if (mo) mo.disconnect();
    try {
      (root || document).querySelectorAll('.money[data-cents]').forEach(convertEl);
    } finally {
      observe();
    }
  }

  // Resolve a currency symbol without a hardcoded table.
  function symbolFor(currency) {
    try {
      var parts = new Intl.NumberFormat('en', { style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol' }).formatToParts(1);
      for (var i = 0; i < parts.length; i++) if (parts[i].type === 'currency') return parts[i].value + ' ';
    } catch (e) {}
    return currency + ' ';
  }

  // Wire conversion (observer + initial pass). Idempotent.
  var started = false;
  function startConversion() {
    if (started) { convertAll(); return; }
    started = true;
    document.documentElement.classList.add('diorin-fx-on');
    mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) {
          clearTimeout(moTimer);
          moTimer = setTimeout(function () { convertAll(); }, 50);
          return;
        }
      }
    });
    var run = function () { convertAll(); };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
    else run();
    document.addEventListener('cart:refresh', function () { convertAll(); });
    log('conversion ON', fx);
  }

  // Country → currency for rates we may carry in settings.fx_rates.
  // Anything not listed (and any listed currency missing a rate) shows USD.
  var EURO = 'AT BE CY DE EE ES FI FR GR HR IE IT LT LU LV MT NL PT SI SK MC SM AD';
  var COUNTRY_CCY = {
    US: 'USD', AE: 'AED', GB: 'GBP', AU: 'AUD', CA: 'CAD',
    SA: 'SAR', KW: 'KWD', QA: 'QAR', OM: 'OMR', BH: 'BHD',
    SG: 'SGD', MY: 'MYR', NZ: 'NZD', JP: 'JPY', HK: 'HKD', CH: 'CHF'
  };
  function currencyFor(country) {
    if (EURO.indexOf(country) > -1) return 'EUR';
    return COUNTRY_CCY[country] || 'USD';
  }

  // Apply a detected country/currency, then convert.
  function applyCurrency(country, currency) {
    if (!country || country === 'IN' || currency === 'INR') { log('skip (India/INR)', country, currency); return false; }
    // Flag the country even if we end up unable to convert prices:
    // hides .india-only promos and switches checkout routing to native Shopify.
    if (document.body) document.body.setAttribute('data-diorin-country', country);
    var rate = rates[currency];
    if (!rate && currency !== 'USD') { log('no rate for', currency, '— falling back to USD'); currency = 'USD'; rate = rates.USD; }
    if (!rate) { log('no USD rate available — cannot convert'); return false; }
    fx.country = country;
    fx.currency = currency;
    fx.rate = rate;
    fx.symbol = symbolFor(currency);
    startConversion();
    return true;
  }

  // Live FX (INR base), cached 12h. settings.fx_rates stays as fallback.
  // No markup here — international markup is Shopify Markets' price adjustment.
  function applyLive(live) {
    for (var c in live) if (live[c] > 0) rates[c] = live[c];
    log('live rates applied (pure market rate)');
  }
  function loadRates() {
    var KEY = 'diorin_fx_v1';
    var cached = null;
    try { cached = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) {}
    if (cached && cached.t && Date.now() - cached.t < 12 * 36e5 && cached.r) {
      applyLive(cached.r); return Promise.resolve();
    }
    return fetch('https://open.er-api.com/v6/latest/INR')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || d.result !== 'success' || !d.rates) return log('rate feed bad payload — using fallback fx_rates');
        try { localStorage.setItem(KEY, JSON.stringify({ r: d.rates, t: Date.now() })); } catch (e) {}
        applyLive(d.rates);
      })
      .catch(function (e) { log('rate feed failed — using fallback fx_rates', e); });
  }

  // Public API
  cfg.formatMoney = formatMoney;
  cfg.convertAll = convertAll;
  cfg.fxActive = active;
  // Console test helper: Diorin.preview('AED') — forces a currency, no Shopify/VPN needed.
  cfg.preview = function (currency) { return applyCurrency('XX', currency); };

  // If the visitor's country belongs to a configured Shopify Market but the
  // page was rendered in the default (India) context, switch context so Liquid
  // re-renders with the market's price adjustment baked into the INR amounts.
  // One reload per visitor; the localization cookie then persists ~1 year.
  function syncMarket(country) {
    if (!country || country === serverCountry) return false;         // already in the right context
    if (!cfg.markets || cfg.markets.indexOf(country) === -1) return false; // country not in any market → plain INR conversion
    var FLAG = 'diorin_mkt';
    try {
      if (sessionStorage.getItem(FLAG) === country) return false;    // already tried this session — don't loop
      sessionStorage.setItem(FLAG, country);
    } catch (e) { return false; }
    log('switching market context →', country);
    fetch('/localization', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: 'form_type=localization&utf8=%E2%9C%93&_method=put&country_code=' + country
        + '&return_to=' + encodeURIComponent(location.pathname + location.search)
    })
      .then(function (r) { if (r.ok) location.reload(); })
      .catch(function (e) { log('market switch failed', e); });
    return true;
  }

  function geoDetect() {
    var KEY = 'diorin_geo';
    try { localStorage.removeItem(KEY); } catch (e) {} // purge stale 7-day cache from old ipapi version
    var handle = function (country) {
      applyCurrency(country, currencyFor(country));
      syncMarket(country);
    };
    var cached = null;
    try { cached = sessionStorage.getItem(KEY); } catch (e) {}
    if (cached) { log('geo session cache', cached); handle(cached); return; }
    // Shopify's own geo endpoint: same-origin, no rate limits, not on adblock lists.
    fetch('/browsing_context_suggestions.json')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var country = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
        log('geo detected', country);
        if (!country) return;
        try { sessionStorage.setItem(KEY, country); } catch (e) {}
        handle(country);
      })
      .catch(function (e) { log('geo failed', e); });
  }

  // ── Checkout routing: Shiprocket for India only ──
  // (Cart drawer handled by .diorin-intl-checkout in cart-drawer.liquid: Shiprocket
  // deletes button[name=checkout] nodes outright, so that button dodges its selectors.)
  function isIndia() {
    return ((document.body && document.body.getAttribute('data-diorin-country')) || 'IN') === 'IN';
  }
  cfg.isIndia = isIndia;

  // Boot
  if (cfg.enabled) {
    var ratesReady = loadRates();      // live FX x markup (12h cache); fx_rates = fallback
    if (active()) {
      startConversion();               // Shopify already resolved a foreign country
      ratesReady.then(function () {
        if (rates[fx.currency]) { fx.rate = rates[fx.currency]; convertAll(); }
      });
    } else {
      ratesReady.then(geoDetect);      // detect via IP (real visitors + VPN)
    }
  } else if (active()) {
    startConversion();
  } else {
    log('disabled (enable_currency_conversion off)');
  }

  // Self-check (?debug)
  if (DEBUG) {
    var saved = { rate: fx.rate, country: fx.country, currency: fx.currency, symbol: fx.symbol, showCode: fx.showCode };
    fx.rate = 0.012; fx.country = 'US'; fx.currency = 'USD'; fx.symbol = '$'; fx.showCode = false;
    console.assert(formatMoney(99900) === '$12', 'FAIL 99900@0.012 => ' + formatMoney(99900));
    fx.country = 'IN';
    console.assert(formatMoney(99900) === '₹999', 'FAIL IN fallback => ' + formatMoney(99900));
    fx.rate = saved.rate; fx.country = saved.country; fx.currency = saved.currency; fx.symbol = saved.symbol; fx.showCode = saved.showCode;
    log('loaded. enabled=' + cfg.enabled + ' rates=', rates, 'fx=', fx);
  }
})();
